import BusinessIcon from '@mui/icons-material/Business';
import CalculateIcon from '@mui/icons-material/Calculate';
import FilePresentIcon from '@mui/icons-material/FilePresent';
import { Stack } from '@mui/material';
import { useConfirmingRequestOverview } from 'lib/api/confirming';
import { formatDateString, formatMoney } from 'lib/functions/utils';
import {
  ContentCard,
  ErrorState,
  KeyValueCard,
  StatNumber,
  TabContentSkeleton,
} from 'lib/mobileDesignSystem';
import React, { useEffect, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';

import { useConfirmingOperationDetail } from '../context';

export interface OverviewProps {
  requestId: string;
}

const Overview: React.FC<OverviewProps> = ({ requestId }) => {
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const { setOperationStatus } = useConfirmingOperationDetail();
  const {
    data: operation,
    isLoading,
    isError,
  } = useConfirmingRequestOverview(dispatch, requestId);

  useEffect(() => {
    if (operation?.status != null) {
      setOperationStatus(operation.status);
    }
  }, [operation?.status, setOperationStatus]);

  const costItems = useMemo(() => {
    if (!operation) return [];
    const sim = operation.simulation;
    if (!sim) {
      return [
        {
          label: 'Monto operación',
          value: `${formatMoney(operation.amount)} CLP`,
        },
        {
          label: 'Detalles',
          value: 'Sin documentos activos para simular',
        },
      ];
    }
    const commissionTotal =
      (operation.operationCostFixed ?? 0) +
      (operation.operationCostVariable ?? 0) +
      sim.operationIva;
    const expensesTotal = (operation.expensesCost ?? 0) + sim.expensesIva;

    return [
      {
        label: 'Monto operación',
        value: `${formatMoney(operation.amount)} CLP`,
      },
      {
        label: `Monto a financiar (${operation.advance}%)`,
        value: `${formatMoney(sim.operationAdvanced)} CLP`,
      },
      {
        label: `Tasa del fondo (${operation.fundRate}%)`,
        value: `${formatMoney(sim.operationCostFundRate)} CLP`,
      },
      {
        label: `Spread (${sim.spread}%)`,
        value: `${formatMoney(sim.operationCostSpread)} CLP`,
      },
      {
        label: 'Comisión operación + IVA',
        value: `${formatMoney(commissionTotal)} CLP`,
      },
      {
        label: `Otros gastos${operation.expensesHasIva ? ' + IVA' : ''}`,
        value: `${formatMoney(expensesTotal)} CLP`,
      },
      {
        label: 'Cuenta por cobrar',
        value: `${formatMoney(sim.operationAccountReceivable)} CLP`,
      },
      {
        label: 'Monto a girar',
        value: `${formatMoney(sim.amountToDeposit)} CLP`,
      },
    ];
  }, [operation]);

  if (isLoading) return <TabContentSkeleton />;

  if (isError)
    return <ErrorState message="Ocurrió un error al cargar el detalle." />;

  if (!operation) return <ErrorState message="No se encontró la operación." />;

  return (
    <Stack spacing={2} sx={{ pt: 1 }}>
      <ContentCard
        header={{
          icon: <BusinessIcon fontSize="small" />,
          title: operation.stakeholderName,
          description: operation.stakeholderIdentifier,
        }}
      >
        <Stack spacing={1} direction="row">
          <StatNumber label="Anticipo" amount={operation.advance} tag="%" />
          <StatNumber label="Interés" amount={operation.rate} tag="%" />
          <StatNumber label="Fondo" amount={operation.fundRate} tag="%" />
          <StatNumber label="Mora" amount={operation.arrearRate} tag="%" />
        </Stack>
      </ContentCard>
      <ContentCard
        header={{
          icon: <CalculateIcon sx={{ fontSize: 20 }} />,
          title: 'Detalles de operación',
          description: formatDateString(
            operation.requestDate != null
              ? typeof operation.requestDate === 'string'
                ? operation.requestDate
                : operation.requestDate.toISOString()
              : null,
            'dd-mm-yyyy',
          ),
        }}
      >
        <KeyValueCard items={costItems} highlightLastRows={2} />
      </ContentCard>
      <ContentCard
        header={{
          icon: <FilePresentIcon sx={{ fontSize: 20 }} />,
          title: 'Documentos',
          description: `${(operation.documents ?? []).length} documento${(operation.documents ?? []).length !== 1 ? 's' : ''}`,
          clickable: true,
          onClick: () => navigate(`/finance/confirming/${requestId}/documents`),
        }}
      />
    </Stack>
  );
};

export default Overview;
